import {
  createPublicClient,
  formatUnits,
  getAddress,
  http,
  parseAbi,
  type Address,
} from "viem";
import { avalanche } from "viem/chains";
import {
  AIAVAX_ADDRESS,
  AVAX_USD_FEED_ADDRESS,
  CHAIN_ID,
  ORACLE_STALE_AFTER_SECONDS,
  RPC_URL,
  WALLET_ADDRESS,
  WAVAX_ADDRESS,
  sameAddress,
} from "./config";
import type { PortfolioSnapshot } from "./types";

const vaultAbi = parseAbi(["function hook() view returns (address)"]);
const tellerAbi = parseAbi([
  "function vault() view returns (address)",
  "function accountant() view returns (address)",
]);
const accountantAbi = parseAbi([
  "function base() view returns (address)",
  "function decimals() view returns (uint8)",
  "function getRateSafe() view returns (uint256)",
  "function getRateInQuoteSafe(address quote) view returns (uint256)",
]);
const erc20Abi = parseAbi([
  "function balanceOf(address account) view returns (uint256)",
  "function decimals() view returns (uint8)",
]);
const chainlinkAbi = parseAbi([
  "function decimals() view returns (uint8)",
  "function latestRoundData() view returns (uint80 roundId, int256 answer, uint256 startedAt, uint256 updatedAt, uint80 answeredInRound)",
]);

interface ReadContractRequest {
  address: Address;
  abi: readonly unknown[];
  functionName: string;
  args?: readonly unknown[];
}

export type ContractRead = (request: ReadContractRequest) => Promise<unknown>;

export interface AiAvaxRateResult {
  aiAvaxPerAvax: number;
  teller: Address;
  accountant: Address;
  accountantBase: Address;
}

export const publicClient = createPublicClient({
  chain: avalanche,
  transport: http(RPC_URL, {
    timeout: 15_000,
    retryCount: 2,
    retryDelay: 400,
  }),
});

function asAddress(value: unknown, label: string): Address {
  if (typeof value !== "string") {
    throw new Error(`${label} 返回了无效地址`);
  }
  try {
    return getAddress(value);
  } catch {
    throw new Error(`${label} 返回了无效地址`);
  }
}

function asInteger(value: unknown, label: string): number {
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    throw new Error(`${label} 返回了无效整数`);
  }
  return parsed;
}

function asBigInt(value: unknown, label: string): bigint {
  if (typeof value !== "bigint") {
    throw new Error(`${label} 返回了无效数值`);
  }
  return value;
}

export function positiveUnitsToNumber(
  value: bigint,
  decimals: number,
  label: string,
): number {
  if (value <= 0n) throw new Error(`${label} 必须大于零`);
  const result = Number(formatUnits(value, decimals));
  if (!Number.isFinite(result) || result <= 0) {
    throw new Error(`${label} 无法转换为有效价格`);
  }
  return result;
}

export async function resolveAiAvaxRate(
  readContract: ContractRead,
): Promise<AiAvaxRateResult> {
  const teller = asAddress(
    await readContract({
      address: AIAVAX_ADDRESS,
      abi: vaultAbi,
      functionName: "hook",
    }),
    "aiAVAX hook",
  );

  const tellerVault = asAddress(
    await readContract({
      address: teller,
      abi: tellerAbi,
      functionName: "vault",
    }),
    "Teller vault",
  );
  if (!sameAddress(tellerVault, AIAVAX_ADDRESS)) {
    throw new Error("aiAVAX 的 hook 已不再指向其 Teller，已停止价格计算");
  }

  const accountant = asAddress(
    await readContract({
      address: teller,
      abi: tellerAbi,
      functionName: "accountant",
    }),
    "Teller accountant",
  );
  const accountantBase = asAddress(
    await readContract({
      address: accountant,
      abi: accountantAbi,
      functionName: "base",
    }),
    "Accountant base",
  );

  let rawRate: bigint;
  let rateDecimals: number;
  if (sameAddress(accountantBase, WAVAX_ADDRESS)) {
    const [decimalsValue, rateValue] = await Promise.all([
      readContract({
        address: accountant,
        abi: accountantAbi,
        functionName: "decimals",
      }),
      readContract({
        address: accountant,
        abi: accountantAbi,
        functionName: "getRateSafe",
      }),
    ]);
    rateDecimals = asInteger(decimalsValue, "Accountant decimals");
    rawRate = asBigInt(rateValue, "Accountant rate");
  } else {
    const [decimalsValue, rateValue] = await Promise.all([
      readContract({
        address: WAVAX_ADDRESS,
        abi: erc20Abi,
        functionName: "decimals",
      }),
      readContract({
        address: accountant,
        abi: accountantAbi,
        functionName: "getRateInQuoteSafe",
        args: [WAVAX_ADDRESS],
      }),
    ]);
    rateDecimals = asInteger(decimalsValue, "WAVAX decimals");
    rawRate = asBigInt(rateValue, "WAVAX quote rate");
  }

  return {
    aiAvaxPerAvax: positiveUnitsToNumber(
      rawRate,
      rateDecimals,
      "aiAVAX/AVAX 价格",
    ),
    teller,
    accountant,
    accountantBase,
  };
}

export function getOracleState(
  answer: bigint,
  decimals: number,
  updatedAt: bigint,
  nowSeconds = Math.floor(Date.now() / 1_000),
) {
  const avaxUsd = positiveUnitsToNumber(answer, decimals, "AVAX/USD 价格");
  if (updatedAt <= 0n) throw new Error("Chainlink 返回了无效更新时间");
  const updatedAtNumber = Number(updatedAt);
  if (!Number.isSafeInteger(updatedAtNumber)) {
    throw new Error("Chainlink 更新时间超出有效范围");
  }
  const ageSeconds = Math.max(0, nowSeconds - updatedAtNumber);
  return {
    avaxUsd,
    updatedAt: new Date(updatedAtNumber * 1_000),
    ageSeconds,
    isStale: ageSeconds > ORACLE_STALE_AFTER_SECONDS,
  };
}

export async function fetchPortfolioSnapshot(): Promise<PortfolioSnapshot> {
  const readContract: ContractRead = (request) =>
    publicClient.readContract(request as never) as Promise<unknown>;

  const chainId = await publicClient.getChainId();
  if (chainId !== CHAIN_ID) {
    throw new Error(`网络错误：当前 chain ID 为 ${chainId}，应为 ${CHAIN_ID}`);
  }

  const [rate, avaxBalance, aiAvaxBalanceValue, aiAvaxDecimalsValue, blockNumber, feedDecimalsValue, roundDataValue] =
    await Promise.all([
      resolveAiAvaxRate(readContract),
      publicClient.getBalance({ address: WALLET_ADDRESS }),
      readContract({
        address: AIAVAX_ADDRESS,
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [WALLET_ADDRESS],
      }),
      readContract({
        address: AIAVAX_ADDRESS,
        abi: erc20Abi,
        functionName: "decimals",
      }),
      publicClient.getBlockNumber(),
      readContract({
        address: AVAX_USD_FEED_ADDRESS,
        abi: chainlinkAbi,
        functionName: "decimals",
      }),
      readContract({
        address: AVAX_USD_FEED_ADDRESS,
        abi: chainlinkAbi,
        functionName: "latestRoundData",
      }),
    ]);

  if (!Array.isArray(roundDataValue) || roundDataValue.length < 4) {
    throw new Error("Chainlink 返回了无效轮次数据");
  }
  const answer = asBigInt(roundDataValue[1], "Chainlink answer");
  const updatedAt = asBigInt(roundDataValue[3], "Chainlink updatedAt");
  const feedDecimals = asInteger(feedDecimalsValue, "Chainlink decimals");
  const oracle = getOracleState(answer, feedDecimals, updatedAt);
  const aiAvaxDecimals = asInteger(aiAvaxDecimalsValue, "aiAVAX decimals");
  const aiAvaxBalance = asBigInt(aiAvaxBalanceValue, "aiAVAX balance");

  return {
    capturedAt: new Date(),
    blockNumber,
    avaxBalance,
    aiAvaxBalance,
    aiAvaxDecimals,
    avaxUsd: oracle.avaxUsd,
    aiAvaxPerAvax: rate.aiAvaxPerAvax,
    aiAvaxUsd: rate.aiAvaxPerAvax * oracle.avaxUsd,
    oracleUpdatedAt: oracle.updatedAt,
    oracleAgeSeconds: oracle.ageSeconds,
    oracleIsStale: oracle.isStale,
    teller: rate.teller,
    accountant: rate.accountant,
    accountantBase: rate.accountantBase,
  };
}
